import React, { PropsWithChildren, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AnalyticsPlot } from '../../analytics/types/analytics.types';
import { Svg } from '../components/Svg';
import { useCell } from '../hooks/useCell';
import { sizeSelector, subSizeSelector } from '../selectors/cell.selectors';
import { CellId } from '../types/cell.type';

interface Context {
  rootId: CellId;
  plotRef: React.RefObject<SVGSVGElement>;
  foregroundRef: React.RefObject<SVGGElement>;
  backgroundRef: React.RefObject<SVGGElement>;
  plotName: AnalyticsPlot | undefined;
}

const PlotContext = React.createContext<Context | null>(null);
type Props = PropsWithChildren<Omit<React.SVGProps<SVGSVGElement>, 'ref'>> & {
  plotName?: AnalyticsPlot;
};

export const Plot: React.FC<Props> = ({ children, plotName, ...svgProps }) => {
  const rootId = useCell({ type: 'root' });
  const height = useSelector(sizeSelector(rootId));
  const width = useSelector(subSizeSelector(rootId));
  const plotRef = useRef<SVGSVGElement>(null);
  const foregroundRef = useRef<SVGGElement>(null);
  const backgroundRef = useRef<SVGGElement>(null);

  return (
    <Svg view={{ width, height }} ref={plotRef} {...svgProps}>
      <g className='background' ref={backgroundRef} />
      <PlotContext.Provider
        value={{ rootId, plotRef, foregroundRef, backgroundRef, plotName }}
      >
        {children}
      </PlotContext.Provider>
      <g className='foreground' ref={foregroundRef} />
    </Svg>
  );
};

export const usePlot = () => {
  const context = useContext(PlotContext);
  if (context == null) {
    throw new Error('usePlot must be used inside <Plot />');
  }

  return context;
};
